'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import { usePathname } from 'next/navigation';
import { ProductDetailsProps } from '../../ProductDetails';
import { pushNavInteractionDefault } from './navTracking';
import { StickyNavigationBarDesktop } from './StickyNavigationBarDesktop';
import { StickyNavigationBarMobile } from './StickyNavigationBarMobile';

// Default sticky offset (px) for scroll-spy / smooth-scroll. TI passes a
// pinned-height-derived value so spy and scroll stay in sync (CMS-1643 AC5/AC6).
export const DEFAULT_STICKY_OFFSET = 64;

// Active-state accent for the sticky nav. Centralised here to remove duplication.
// TODO(CMS-1643): confirm the TI active colour with design and move to a design
// token; kept as the existing hex so the shared CC/PL nav does not regress.
export const STICKY_NAV_ACTIVE_TEXT = 'text-[#E87722]';
export const STICKY_NAV_ACTIVE_BORDER = 'border-[#E87722]';

export type StickyNavigationBarProps = ProductDetailsProps & {
  /**
   * Section tabs in render order, derived from the builder's sections — the nav never decides
   * which sections exist.
   */
  navigationItems: INavigationItem[];
  SPACER_COUNT?: number;
};

/**
 * Default sticky navigation (CC, PL, STK / BKA and every vertical without its own nav): a plain
 * CSS-`sticky` bar, desktop links >= md and a mobile dropdown < md. No Apply CTA: only the TI
 * provider page keeps one (CMS-1924, see `showApplyCta`).
 */
export const StickyNavigationBar: React.FC<StickyNavigationBarProps> = (
  props,
) => {
  const { navigationItems } = props;
  const stickyOffset = props.stickyOffset ?? DEFAULT_STICKY_OFFSET;
  // Product-list name for the interaction events fired from the nav. Uses the
  // page path, matching the value the Product Hero sends, so all PDP events
  // carry a consistent, populated item_list_name instead of undefined.
  const itemListName = usePathname() ?? undefined;

  if (navigationItems.length === 0) {
    return null;
  }

  return (
    <>
      <div className="hidden md:block sticky top-0 z-20 bg-fill/alternative-1">
        <div className="max-w-[--breakpoints-content-narrow-3-lg] w-full lg:mx-auto px-spacing/padding/section-y lg:px-0">
          <StickyNavigationBarDesktop
            {...props}
            navigationItems={navigationItems}
            SPACER_COUNT={props.SPACER_COUNT}
            stickyOffset={stickyOffset}
            itemListName={itemListName}
            pushNavInteraction={pushNavInteractionDefault}
          />
        </div>
      </div>
      <div className="block md:hidden sticky top-0 z-20 bg-fill/alternative-1 px-spacing/padding/section-y lg:px-0">
        <StickyNavigationBarMobile
          {...props}
          navigationItems={navigationItems}
          stickyOffset={stickyOffset}
          itemListName={itemListName}
          pushNavInteraction={pushNavInteractionDefault}
        />
      </div>
    </>
  );
};

export interface INavigationItem {
  label: string;
  id: string;
}
